import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Calendar, Users, DollarSign, BarChart3, Shield, Zap,
  CheckCircle, ArrowRight, Menu, X, Star, Quote,
  Building2, Clock, CreditCard, Globe, Smartphone, Lock,
  TrendingUp, Award, ChevronRight, Mail, Phone, MapPin
} from 'lucide-react'
import { Button } from '../components/ui/Button'

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setMobileMenuOpen(false)
    }
  }

  const features = [
    {
      icon: Calendar,
      title: 'Smart Booking System',
      description: 'Intuitive scheduling with real-time availability, automated confirmations, and calendar sync.'
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Comprehensive client profiles, booking history, and automated communications.'
    },
    {
      icon: DollarSign,
      title: 'Revenue Tracking',
      description: 'Real-time financial insights, revenue forecasting, and detailed analytics.'
    },
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Data-driven insights on utilization, revenue trends, and client behavior.'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with automated backups and 99.9% uptime.'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Built on Cloudflare Workers for instant performance worldwide.'
    }
  ]

  const benefits = [
    'Reduce no-shows by up to 80% with automated reminders',
    'Save 10+ hours per week on administrative tasks',
    'Increase revenue by optimizing studio utilization',
    'Accept payments seamlessly with integrated processing',
    'Access your business from anywhere, anytime',
    'Scale effortlessly as your business grows'
  ]

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Owner, Rhythm Studios London',
      image: null,
      quote: 'StudioManager 360 transformed our booking process. We\'ve seen a 40% increase in bookings and significantly reduced admin time.'
    },
    {
      name: 'James Chen',
      role: 'Director, SoundWave Productions',
      image: null,
      quote: 'The analytics features helped us identify peak times and optimize pricing. Our revenue is up 25% since switching.'
    },
    {
      name: 'Emma Thompson',
      role: 'Manager, Creative Space Hub',
      image: null,
      quote: 'Client management has never been easier. The automated reminders alone have cut our no-shows in half.'
    }
  ]

  const pricingPlans = [
    {
      name: 'Starter',
      price: '£29',
      period: 'per month',
      description: 'Perfect for small studios just getting started',
      features: [
        'Up to 2 studio spaces',
        '100 bookings per month',
        'Basic analytics',
        'Email support',
        'Mobile app access',
        'Client management'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Professional',
      price: '£79',
      period: 'per month',
      description: 'Ideal for growing studio businesses',
      features: [
        'Up to 10 studio spaces',
        'Unlimited bookings',
        'Advanced analytics',
        'Priority support',
        'Custom branding',
        'Payment processing',
        'API access',
        'Revenue forecasting'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact us',
      description: 'For large organizations with specific needs',
      features: [
        'Unlimited studios',
        'Unlimited bookings',
        'Dedicated support',
        'Custom integrations',
        'SLA guarantee',
        'Training & onboarding',
        'Multi-tenant support',
        'White-label options'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                StudioManager 360
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                Features
              </button>
              <button onClick={() => scrollToSection('benefits')} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                Benefits
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                Pricing
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                Contact
              </button>
              <Link to="/login">
                <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/10">
                  Sign In
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
            <div className="px-4 py-3 space-y-3">
              <button onClick={() => scrollToSection('features')} className="block w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                Features
              </button>
              <button onClick={() => scrollToSection('benefits')} className="block w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                Benefits
              </button>
              <button onClick={() => scrollToSection('pricing')} className="block w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                Pricing
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                Contact
              </button>
              <Link to="/login" className="block">
                <Button variant="outline" className="w-full border-orange-600 text-orange-600">
                  Sign In
                </Button>
              </Link>
              <Link to="/signup" className="block">
                <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-500/10 rounded-full mb-6">
              <Award className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-orange-900 dark:text-orange-100">
                Trusted by 500+ Studios Worldwide
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6">
              Studio Management,
              <span className="block mt-2 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Simplified 360°
              </span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              The all-in-one platform for managing bookings, clients, and revenue.
              Built for creative studios, rehearsal spaces, and rental facilities.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup">
                <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-lg px-8 py-6 flex items-center gap-2">
                  Start Free 14-Day Trial
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <button
                onClick={() => scrollToSection('features')}
                className="text-lg px-8 py-6 border-2 border-gray-300 dark:border-gray-700 rounded-xl hover:border-orange-600 dark:hover:border-orange-400 transition-colors flex items-center gap-2"
              >
                Watch Demo
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-6 text-sm text-gray-500 dark:text-gray-500">
              No credit card required · Cancel anytime · Free onboarding support
            </p>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">500+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Active Studios</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">50K+</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Monthly Bookings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">99.9%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">4.9/5</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Customer Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Everything You Need to
              <span className="block mt-2 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Run Your Studio
              </span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Powerful features designed specifically for studio management professionals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all duration-300 hover:shadow-xl group"
              >
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Why Studios Choose
                <span className="block mt-2 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  StudioManager 360
                </span>
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                Join hundreds of successful studios that have streamlined their operations and increased revenue with our platform.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mt-0.5">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <p className="text-lg text-gray-700 dark:text-gray-300">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                    <Clock className="h-8 w-8 text-orange-600 dark:text-orange-400 mb-3" />
                    <h3 className="font-semibold text-lg mb-2">24/7 Access</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Manage your studio anytime, anywhere</p>
                  </div>
                  <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                    <CreditCard className="h-8 w-8 text-orange-600 dark:text-orange-400 mb-3" />
                    <h3 className="font-semibold text-lg mb-2">Payment Processing</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Accept payments seamlessly</p>
                  </div>
                </div>
                <div className="space-y-6 pt-12">
                  <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                    <Smartphone className="h-8 w-8 text-orange-600 dark:text-orange-400 mb-3" />
                    <h3 className="font-semibold text-lg mb-2">Mobile Ready</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">iOS and Android apps included</p>
                  </div>
                  <div className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
                    <Lock className="h-8 w-8 text-orange-600 dark:text-orange-400 mb-3" />
                    <h3 className="font-semibold text-lg mb-2">Secure Data</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Enterprise-grade security</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Loved by Studio Owners
              <span className="block mt-2 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Worldwide
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all duration-300 hover:shadow-xl"
              >
                <Quote className="h-8 w-8 text-orange-600 dark:text-orange-400 mb-4" />
                <p className="text-gray-700 dark:text-gray-300 mb-6">{testimonial.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-orange-500 fill-current" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Choose the perfect plan for your studio. All plans include a 14-day free trial.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-2xl ${
                  plan.popular
                    ? 'border-4 border-orange-500 shadow-2xl scale-105'
                    : 'border-2 border-gray-200 dark:border-gray-800'
                } bg-white dark:bg-gray-800`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-gray-600 dark:text-gray-400"> / {plan.period}</span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block">
                  <Button
                    className={`w-full ${
                      plan.popular
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                        : 'bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Have questions? We're here to help you get started.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2">Email Us</h3>
              <a href="mailto:hello@studiomgr360.com" className="text-orange-600 dark:text-orange-400 hover:underline">
                hello@studiomgr360.com
              </a>
            </div>
            <div className="text-center p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-4">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2">Call Us</h3>
              <a href="tel:+442012345678" className="text-orange-600 dark:text-orange-400 hover:underline">
                +44 20 1234 5678
              </a>
            </div>
            <div className="text-center p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2">Visit Us</h3>
              <p className="text-gray-600 dark:text-gray-400">
                London, United Kingdom
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-600 to-red-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Studio Management?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of studios already using StudioManager 360. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup">
              <Button className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6 flex items-center gap-2">
                Start Free Trial
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-lg px-8 py-6 border-2 border-white text-white rounded-xl hover:bg-white/10 transition-colors"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <span className="text-lg font-bold text-white">
                  StudioManager 360
                </span>
              </div>
              <p className="text-sm">
                The complete solution for studio management professionals worldwide.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-orange-400">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-orange-400">Pricing</button></li>
                <li><Link to="/signup" className="hover:text-orange-400">Sign Up</Link></li>
                <li><Link to="/login" className="hover:text-orange-400">Sign In</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-orange-400">About Us</a></li>
                <li><a href="#" className="hover:text-orange-400">Careers</a></li>
                <li><a href="#" className="hover:text-orange-400">Blog</a></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-orange-400">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-orange-400">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-orange-400">Terms of Service</a></li>
                <li><a href="#" className="hover:text-orange-400">Cookie Policy</a></li>
                <li><a href="#" className="hover:text-orange-400">GDPR</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} StudioManager 360. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
